import { shuffle } from 'es-toolkit';

import {
    CategorySearchResult,
    FetchCategoryResultsParams,
    FetchSearchResultsParams,
    SearchResult,
} from '@/redux/modules/search/search.types';

import { ApiHosts, handleResponseOld, handleResponseWithNon200Errors, makeGet } from '../../api/helpers';

export const transformSearchResult = (searchResult: SearchResult) => {
    if (typeof searchResult.payload === 'object' && searchResult.payload?.itemIDs) {
        searchResult.payload.itemIDs = shuffle(searchResult.payload.itemIDs);
    }
    return {
        error: searchResult.error,
        payload: searchResult.payload,
    };
};

/**
 * fetches category search results via Get to Search API
 * @function fetchCategoryResults
 * @category Search API
 * @param {FetchCategoryResultsParams}
 * @return {Promise<CategorySearchResult>}
 * @see https://search-party-DEPLOYMENT.liveauctioneers.com/search/v4/categoryweb?ids=${encodedIds}&parameters=${encodedParams}
 */
export const fetchCategoryResults = ({
    aggsOnly,
    authToken,
    deployment,
    ids,
    ipAddress,
    searchQuery,
    skipAggs,
    useScoutCategories,
}: FetchCategoryResultsParams): Promise<CategorySearchResult> =>
    new Promise<CategorySearchResult>((resolve, reject) => {
        const encodedIds = encodeURIComponent(JSON.stringify(ids));
        const { buyNow, ...queryWithoutBuyNow } = searchQuery;
        const buyNowParam = buyNow ? `&buyNow=${encodeURIComponent(JSON.stringify(buyNow))}` : '';
        let searchString = `${ApiHosts.Search}?ids=${encodedIds}${buyNowParam}`;
        const encodedParams = encodeURIComponent(JSON.stringify(queryWithoutBuyNow));
        searchString = `${searchString}&parameters=${encodedParams}`;

        const request = makeGet({
            apiPath: 'v4/categoryweb',
            authToken,
            deployment,
            ipAddress,
            // @ts-expect-error
            //  path *wants* an ApiHosts enum value.
            //  However, this API request did some complex URL building logic.
            //  Thus, I will leave it here until some brave soul chooses to fix it.
            path: searchString,
        });

        request.query({
            ...(aggsOnly && { aggsOnly }),
            ...(skipAggs && { skipAggs }),
            ...(useScoutCategories && { useScoutCategories }),
        });

        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Search API
 * @see fetchPromotedSearchResults
 */
type FetchPromotedSearchResultsParams = {
    authToken: string;
    deployment: string;
    ids?: any;
    ipAddress: string;
    rankingVersionVariant?: string;
    searchQuery: any;
    useScoutCategories?: boolean;
};
/**
 * fetches promoted search results via Get to Search API
 * @function fetchPromotedSearchResults
 * @category Search API
 * @param {FetchPromotedSearchResultsParams}
 * @return {Promise<SearchResult>}
 * @see https://search-party-DEPLOYMENT.liveauctioneers.com/search/v4/categoryweb?ids=${encodedIds}&parameters=${encodedParams}
 * @see https://search-party-DEPLOYMENT.liveauctioneers.com/search/v4/web?ids=${encodedIds}&parameters=${encodedParams}
 */
export const fetchPromotedSearchResults = ({
    authToken,
    deployment,
    ids,
    ipAddress,
    rankingVersionVariant,
    searchQuery,
    useScoutCategories,
}: FetchPromotedSearchResultsParams): Promise<SearchResult> =>
    // TODO should be the new type. (Or a union technically)
    new Promise<SearchResult>((resolve, reject) => {
        let searchString = `${ApiHosts.Search}?promoted=1&parameters=${encodeURIComponent(
            JSON.stringify(searchQuery)
        )}`;

        let apiPath = 'v4/web';
        // TODO this should not be done here. Make the API calls just call the API.
        if (ids && ids.length) {
            searchString = `${searchString}&ids=${encodeURIComponent(JSON.stringify(ids))}`;
            apiPath = 'v4/categoryweb';
        } else if (searchQuery.campaignId && searchQuery.campaignId > 0) {
            searchString = `${searchString}&ids=${encodeURIComponent('[]')}`;
            apiPath = 'v4/categoryweb';
        }
        if (rankingVersionVariant) {
            searchString = `${searchString}&rankingVersionVariant=${rankingVersionVariant}`;
        }

        const request = makeGet({
            apiPath,
            authToken,
            deployment,
            ipAddress,
            // @ts-expect-error
            //  path *wants* an ApiHosts enum value.
            //  However, this API request did some complex URL building logic.
            //  Thus, I will leave it here until some brave soul chooses to fix it.
            path: searchString,
        });

        request.query({
            ...(useScoutCategories && { useScoutCategories }),
        });

        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response,
                transform: transformSearchResult,
            })
        );
    });

/**
 * Fetches search results via Get to Search API
 * @function fetchSearchResults
 * @category Search API
 * @param {FetchSearchResultsParams}
 * @param {Promise<SearchResult>}
 * @see https://search-party-DEPLOYMENT.liveauctioneers.com/search/web?parameters=${encodedParams}
 */
export const fetchSearchResults = ({
    aggsOnly,
    authToken,
    deployment,
    geoCoordinates,
    ipAddress,
    popularityScoreReRankingVersion,
    rankingVersionVariant,
    searchQuery,
    skipAggs,
    useGeoLocation = false,
    useScoutCategories,
}: FetchSearchResultsParams): Promise<SearchResult> =>
    new Promise<SearchResult>((resolve, reject) => {
        // Build a local copy to avoid mutating the original searchQuery (which may be
        // cached in Redux via lastSearchFetchOptions).
        const { buyNow, ...queryWithoutBuyNow } = searchQuery;
        const query = {
            ...queryWithoutBuyNow,
            ...(useGeoLocation && { useGeo: true }),
            ...(Boolean(geoCoordinates?.latitude) &&
                Boolean(geoCoordinates?.longitude) && {
                    lat: `${geoCoordinates.latitude}`,
                    long: `${geoCoordinates.longitude}`,
                }),
            /*
             * This is stupid but for some reason it's an array of a string true. Fix this when we fix search :)
             * @see https://liveauctioneers.slack.com/archives/C9LU1NR5Y/p1643056149066600?thread_ts=1643047891.062100&cid=C9LU1NR5Y
             */
            ...(queryWithoutBuyNow.topRated && { topRated: true }),
        };

        const buyNowParam = buyNow ? `&buyNow=${encodeURIComponent(JSON.stringify(buyNow))}` : '';

        let searchString = `${ApiHosts.Search}?parameters=${encodeURIComponent(JSON.stringify(query))}${buyNowParam}`;

        const request = makeGet({
            apiPath: 'v4/web',
            authToken,
            deployment,
            ipAddress,
            // @ts-expect-error
            //  path *wants* an ApiHosts enum value.
            //  However, this API request did some complex URL building logic.
            //  Thus, I will leave it here until some brave soul chooses to fix it.
            path: searchString,
            useCacheKey: false,
        });

        request.query({
            ...(popularityScoreReRankingVersion && { popularityScoreReRankingVersion }),
            ...(aggsOnly && { aggsOnly }),
            ...(rankingVersionVariant && { rankingVersionVariant }),
            ...(skipAggs && { skipAggs }),
            ...(useScoutCategories && { useScoutCategories }),
            useAuctionHouseSearchFiltering: true,
        });

        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });
